import * as Sentry from "@sentry/capacitor";
import { BasicIndex } from "@tanstack/react-db";
import { INITIAL_READ_BATCH_ROWS } from "@/lib/collections/initial-read-batch";
import { powerSyncDb } from "@/lib/powersync";

/**
 * What the PowerSync adapter hands `onDeserializationError` — the Standard
 * Schema failure shape, spelled out here so the app doesn't take a direct
 * dependency on `@standard-schema/spec` for one callback.
 *
 * `path` has to admit the spec's `{ key }` segment alongside the bare key zod
 * actually emits, even though nothing here produces one. The callback is
 * checked contravariantly against the adapter's own signature: narrow this and
 * `collectionBase`'s options stop matching `ConfigWithSQLiteInputType`, at which
 * point the config quietly falls through to the adapter's untyped union member
 * and every collection's row type degrades to the raw SQLite columns.
 */
interface DeserializationFailure {
  readonly issues: readonly {
    readonly message: string;
    readonly path?:
      | readonly (PropertyKey | { readonly key: PropertyKey })[]
      | undefined;
  }[];
}

/**
 * Constant options every PowerSync-backed collection shares: the database
 * handle, eager sync + indexing, no garbage collection, the initial-read batch
 * size, and a labelled deserialization-error reporter.
 *
 * Spread into the object passed to `powerSyncCollectionOptions` alongside the
 * collection's own `table` and `schema`. Those stay at the call site so
 * `powerSyncCollectionOptions` keeps inferring the row type per collection:
 *
 *   export const tasksCollection = createCollection(
 *     powerSyncCollectionOptions({
 *       ...collectionBase("task"),
 *       table: AppSchema.props.tasks,
 *       schema: SelectTaskSchema,
 *     })
 *   );
 */
export function collectionBase(
  label: string,
  // Set where a row's id is itself a credential, so it never reaches telemetry.
  { rowIdIsSecret = false } = {}
) {
  return {
    autoIndex: "eager" as const,
    database: powerSyncDb,
    defaultIndexType: BasicIndex,
    // Never collected. Every collection starts syncing at import, and a cleaned
    // up one stays empty for the readers below until something subscribes.
    gcTime: Number.POSITIVE_INFINITY,
    onDeserializationError: (error: DeserializationFailure, rowId: string) => {
      reportDeserializationFailure(label, error, rowIdIsSecret ? null : rowId);
    },
    syncBatchSize: INITIAL_READ_BATCH_ROWS,
    // Load-bearing. The readiness hooks (`useListSyncing`, `useRowSyncing`) and
    // several write paths read collection state for rows no query asked for,
    // which on-demand mode never loads. ADR-0117 has the measurements.
    syncMode: "eager" as const,
  };
}

/**
 * Collections that have left out a row they could not read this session, for a
 * surface that must not answer from a partial table.
 */
const labelsWithRejectedRows = new Set<string>();
const rejectedRowListeners = new Set<() => void>();

export function subscribeToRejectedRows(listener: () => void): () => void {
  rejectedRowListeners.add(listener);
  return () => {
    rejectedRowListeners.delete(listener);
  };
}

export function hasRejectedRows(label: string): boolean {
  return labelsWithRejectedRows.has(label);
}

/**
 * One report per (collection, offending fields) per session: a schema drift
 * rejects every row it touches, and would otherwise bill one Sentry event per
 * row on the initial load.
 */
const reportedDeserializationFailures = new Set<string>();

/**
 * Surface a row the collection's schema rejected. The patched adapter skips the
 * row and syncs the rest of its batch, so the row is simply missing from the
 * screens above it, invisible unless reported. `syncedRow` keeps a merely-stale
 * row (a column the payload predates) from getting here at all, which leaves
 * this reporting a genuine schema drift.
 */
function reportDeserializationFailure(
  label: string,
  { issues }: DeserializationFailure,
  rowId: string | null
): void {
  if (!labelsWithRejectedRows.has(label)) {
    labelsWithRejectedRows.add(label);
    for (const listener of rejectedRowListeners) {
      listener();
    }
  }

  // Field paths and zod's own messages only — never the offending value, which
  // carries guest PII (see the scrubbers wired in sentry.ts).
  const rejections = issues.map((issue) => ({
    field: (issue.path ?? [])
      .map((segment) =>
        typeof segment === "object" ? String(segment.key) : String(segment)
      )
      .join("."),
    message: issue.message,
  }));
  const fields = rejections.map(({ field }) => field);

  // Ahead of the logging, not just the Sentry call: every `console.error` mints
  // a breadcrumb, so a drift across a whole table would evict the trail of every
  // unrelated error reported after it.
  const seenKey = `${label}|${fields.join(",")}`;
  if (reportedDeserializationFailures.has(seenKey)) {
    return;
  }
  reportedDeserializationFailures.add(seenKey);

  const detail = rejections
    .map(({ field, message }) => `${field || "<row>"}: ${message}`)
    .join("; ");

  console.error(
    `Failed to deserialize ${rowId ? `${label} ${rowId}` : label}: ${detail}`
  );

  Sentry.captureException(
    new Error(`Collection deserialization rejected (${label}): ${detail}`),
    {
      extra: { fields, label, rowId },
      // Group by collection + offending fields so a drift collapses into one
      // issue rather than one per row (the synthetic Error shares a stack).
      fingerprint: ["collection-deserialization", label, ...fields],
      tags: { area: "powersync-sync", collection: label },
    }
  );
}
